import React from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
} from '@mui/material';
import theme from '../../../styles/Themecolors';
import { ItemOption } from '../types';

interface DynamicItemDropdownProps {
  id: string;
  label: string;
  value: ItemOption | null;
  inputValue: string;
  options: ItemOption[];
  loading: boolean;
  isEditing: boolean;
  width?: number | string;
  onSelectionChange: (item: ItemOption | null) => void;
  onInputChange: (value: string, reason: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onOpen: () => void;
  onClose: (event: any, reason: string) => void;
  onListboxScroll: (event: React.UIEvent<HTMLUListElement>) => void;
  getOptionLabel: (option: ItemOption | string) => string;
  hasMore?: boolean;
  loadingMore?: boolean;
}

const DynamicItemDropdown: React.FC<DynamicItemDropdownProps> = ({
  id,
  label,
  value,
  inputValue,
  options,
  loading,
  isEditing,
  width = 200,
  onSelectionChange,
  onInputChange,
  onFocus,
  onBlur,
  onOpen,
  onClose,
  onListboxScroll,
  getOptionLabel,
  hasMore = false,
  loadingMore = false,
}) => {
  return (
    <Box
      sx={{
        width: width,
        minWidth: width,
        '& .MuiAutocomplete-root': {
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: '2px solid #e2e8f0',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
            borderColor: '#cbd5e1',
          },
          '&.Mui-focused': {
            boxShadow: '0 8px 20px rgba(25, 118, 210, 0.15)',
            borderColor: 'primary.main',
          },
        },
      }}
    >
      <Autocomplete
        id={id}
        // freeSolo
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options;
          return options.filter(option => 
            getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
          );
        }}
        onChange={(event, newValue) => {
          if (newValue && typeof newValue !== 'string') {
            onSelectionChange(newValue);
          } else if (newValue === null) {
            onSelectionChange(null);
          }
        }}
        onOpen={onOpen}
        onFocus={onFocus}
        onClose={onClose}
        openOnFocus
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        value={isEditing ? null : value}
        inputValue={inputValue}
        onInputChange={(event, value, reason) => {
          onInputChange(value, reason);
        }}
        getOptionLabel={getOptionLabel}
        options={options}
        loading={loading}
        noOptionsText={loading ? "Loading..." : "No options"}
        ListboxProps={{
          onScroll: onListboxScroll,
          style: { maxHeight: 200, overflow: 'auto' }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            onFocus={onFocus}
            InputLabelProps={{
              sx: {
                color: '#000',
                fontWeight: 700,
                fontSize: 14,
                '&.Mui-focused': { color: '#000' },
                '&.MuiInputLabel-shrink': {
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#000',
                },
              },
            }}
            InputProps={{
              ...params.InputProps,
              sx: {
                '& fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderRadius: '10px',
                  borderWidth: '2px',
                },
                '&:hover fieldset': { borderColor: theme.palette.primary.dark },
                '& .MuiInputBase-input::placeholder': {
                  color: '#000',
                  fontWeight: 700,
                  opacity: 1,
                  fontSize: 12,
                },
              },
              endAdornment: (
                <>
                  {loading || loadingMore ? <CircularProgress color="primary" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option, { index }) => {
          const isLastItem = index === options.length - 1;
          return (
            <li {...props} key={option.value}>
              {getOptionLabel(option)}
              {isLastItem && hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  {loadingMore ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Scroll for more items...
                    </Box>
                  )}
                </Box>
              )}
            </li>
          );
        }}
      />
    </Box>
  );
};

export default DynamicItemDropdown;


// Top 100 films as rated by IMDb users. http://www.imdb.com/chart/top
const top100Films = [
  { label: 'The Shawshank Redemption', year: 1994 },
  { label: 'The Godfather', year: 1972 },
  { label: 'The Godfather: Part II', year: 1974 },
  { label: 'The Dark Knight', year: 2008 },
  { label: '12 Angry Men', year: 1957 },
  { label: "Schindler's List", year: 1993 },
  { label: 'Pulp Fiction', year: 1994 },
  {
    label: 'The Lord of the Rings: The Return of the King',
    year: 2003,
  },
  { label: 'The Good, the Bad and the Ugly', year: 1966 },
  { label: 'Fight Club', year: 1999 },
  {
    label: 'The Lord of the Rings: The Fellowship of the Ring',
    year: 2001,
  },
  {
    label: 'Star Wars: Episode V - The Empire Strikes Back',
    year: 1980,
  },
  { label: 'Forrest Gump', year: 1994 },
  { label: 'Inception', year: 2010 },
  {
    label: 'The Lord of the Rings: The Two Towers',
    year: 2002,
  },
  { label: "One Flew Over the Cuckoo's Nest", year: 1975 },
  { label: 'Goodfellas', year: 1990 },
  { label: 'The Matrix', year: 1999 },
  { label: 'Seven Samurai', year: 1954 },
  {
    label: 'Star Wars: Episode IV - A New Hope',
    year: 1977,
  },
  { label: 'City of God', year: 2002 },
  { label: 'Se7en', year: 1995 },
  { label: 'The Silence of the Lambs', year: 1991 },
  { label: "It's a Wonderful Life", year: 1946 },
  { label: 'Life Is Beautiful', year: 1997 },
  { label: 'The Usual Suspects', year: 1995 },
  { label: 'Léon: The Professional', year: 1994 },
  { label: 'Spirited Away', year: 2001 },
  { label: 'Saving Private Ryan', year: 1998 },
  { label: 'Once Upon a Time in the West', year: 1968 },
  { label: 'American History X', year: 1998 },
  { label: 'Interstellar', year: 2014 },
  { label: 'Casablanca', year: 1942 },
  { label: 'City Lights', year: 1931 },
  { label: 'Psycho', year: 1960 },
  { label: 'The Green Mile', year: 1999 },
  { label: 'The Intouchables', year: 2011 },
  { label: 'Modern Times', year: 1936 },
  { label: 'Raiders of the Lost Ark', year: 1981 },
  { label: 'Rear Window', year: 1954 },
  { label: 'The Pianist', year: 2002 },
  { label: 'The Departed', year: 2006 },
  { label: 'Terminator 2: Judgment Day', year: 1991 },
  { label: 'Back to the Future', year: 1985 },
  { label: 'Whiplash', year: 2014 },
  { label: 'Gladiator', year: 2000 },
  { label: 'Memento', year: 2000 },
  { label: 'The Prestige', year: 2006 },
  { label: 'The Lion King', year: 1994 },
  { label: 'Apocalypse Now', year: 1979 },
  { label: 'Alien', year: 1979 },
  { label: 'Sunset Boulevard', year: 1950 },
  {
    label: 'Dr. Strangelove or: How I Learned to Stop Worrying and Love the Bomb',
    year: 1964,
  },
  { label: 'The Great Dictator', year: 1940 },
  { label: 'Cinema Paradiso', year: 1988 },
  { label: 'The Lives of Others', year: 2006 },
  { label: 'Grave of the Fireflies', year: 1988 },
  { label: 'Paths of Glory', year: 1957 },
  { label: 'Django Unchained', year: 2012 },
  { label: 'The Shining', year: 1980 },
  { label: 'WALL·E', year: 2008 },
  { label: 'American Beauty', year: 1999 },
  { label: 'The Dark Knight Rises', year: 2012 },
  { label: 'Princess Mononoke', year: 1997 },
  { label: 'Aliens', year: 1986 },
  { label: 'Oldboy', year: 2003 },
  { label: 'Once Upon a Time in America', year: 1984 },
  { label: 'Witness for the Prosecution', year: 1957 },
  { label: 'Das Boot', year: 1981 },
  { label: 'Citizen Kane', year: 1941 },
  { label: 'North by Northwest', year: 1959 },
  { label: 'Vertigo', year: 1958 },
  {
    label: 'Star Wars: Episode VI - Return of the Jedi',
    year: 1983,
  },
  { label: 'Reservoir Dogs', year: 1992 },
  { label: 'Braveheart', year: 1995 },
  { label: 'M', year: 1931 },
  { label: 'Requiem for a Dream', year: 2000 },
  { label: 'Amélie', year: 2001 },
  { label: 'A Clockwork Orange', year: 1971 },
  { label: 'Like Stars on Earth', year: 2007 },
  { label: 'Taxi Driver', year: 1976 },
  { label: 'Lawrence of Arabia', year: 1962 },
  { label: 'Double Indemnity', year: 1944 },
  {
    label: 'Eternal Sunshine of the Spotless Mind',
    year: 2004,
  },
  { label: 'Amadeus', year: 1984 },
  { label: 'To Kill a Mockingbird', year: 1962 },
  { label: 'Toy Story 3', year: 2010 },
  { label: 'Logan', year: 2017 },
  { label: 'Full Metal Jacket', year: 1987 },
  { label: 'Dangal', year: 2016 },
  { label: 'The Sting', year: 1973 },
  { label: '2001: A Space Odyssey', year: 1968 },
  { label: "Singin' in the Rain", year: 1952 },
  { label: 'Toy Story', year: 1995 },
  { label: 'Bicycle Thieves', year: 1948 },
  { label: 'The Kid', year: 1921 },
  { label: 'Inglourious Basterds', year: 2009 },
  { label: 'Snatch', year: 2000 },
  { label: '3 Idiots', year: 2009 },
  { label: 'Monty Python and the Holy Grail', year: 1975 },
];


